import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

export const SmartlinkAudioPlayer = ({ previewUrl, title, artist }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
    const onEnded = () => { setPlaying(false); setProgress(0); };
    const onError = () => { setUnavailable(true); setPlaying(false); };
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio || unavailable) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().then(() => setPlaying(true)).catch(() => setUnavailable(true));
    }
  };

  if (unavailable) return null;

  return (
    <div
      data-testid="smartlink-audio-player"
      className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-black/10 shadow-[0_-10px_30px_-10px_rgba(0,0,0,0.3)]"
    >
      <div className="absolute top-0 left-0 h-[2px] bg-brand-red transition-all duration-200" style={{ width: `${progress}%` }} />
      <div className="max-w-md mx-auto flex items-center gap-3 px-4 py-2.5">
        <button
          onClick={toggle}
          data-testid="smartlink-audio-toggle"
          className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center shrink-0 hover:scale-105 transition-transform duration-200"
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-neutral-900 truncate">{title}</p>
          <p className="text-[11px] text-neutral-500 truncate">{artist} · 30s preview</p>
        </div>
      </div>
      <audio ref={audioRef} src={previewUrl} preload="none" />
    </div>
  );
};
